import React, { useState, useEffect } from 'react';
import { getAnalysisSimilars, calculateAnalysisSimilars } from '../../services/analyses';
import { startHeuristicSearch } from '../../services/similarity';
import { getAppConfig } from '../../services/settings';
import ReferenceHistory from '../common/ReferenceHistory';
import './SimilarDrawingsPanel.css';

const SimilarDrawingsPanel = ({ analysisHash, analysisStatus, onViewAnalysis, drawingPageNumbers = [] }) => {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState([]);
  const [searchedHashes, setSearchedHashes] = useState([]);
  const [topOverall, setTopOverall] = useState([]);
  const [currentStage, setCurrentStage] = useState(0);
  const [expandedItem, setExpandedItem] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [modalImage, setModalImage] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [similarityThreshold, setSimilarityThreshold] = useState(null);
  const [heuristicByPage, setHeuristicByPage] = useState({});

  const hasMultiplePages = drawingPageNumbers.length > 1;

  const heuristicPageKey = selectedPage || 'default';
  const heuristicData = heuristicByPage[heuristicPageKey] || {};
  const heuristicStatus = heuristicData.status || 'idle';
  const heuristicResults = heuristicData.results || [];

  useEffect(() => {
    getAppConfig().then(config => setSimilarityThreshold(config.similarity_threshold)).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasMultiplePages && selectedPage === null) {
      setSelectedPage(drawingPageNumbers[0]);
    }
  }, [hasMultiplePages, selectedPage, drawingPageNumbers]);

  useEffect(() => {
    setResults([]);
    setSearchedHashes([]);
    setTopOverall([]);
    setCurrentStage(0);
    setLoading(true);
    setExpandedItem(null);
    setImageUrls({});
    setHeuristicByPage({});

    if (analysisStatus !== 'completed') {
      setLoading(false);
      return;
    }

    const loadCache = async () => {
      try {
        const result = await getAnalysisSimilars(analysisHash);
        if (result.cached && result.data) {
          setResults(result.data.results || []);
          setSearchedHashes(result.data.searched_hashes || []);
          setTopOverall(result.data.top_overall || []);
          setCurrentStage(result.data.stage || 0);
        }
      } catch (error) {
        console.error('Error loading similars cache:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCache();
  }, [analysisHash, analysisStatus]);

  const handleSearch = async (stage) => {
    setCalculating(true);
    const pageToSearch = hasMultiplePages ? selectedPage : null;
    const existingAboveCount = stage === 3 && similarityThreshold !== null
      ? results.filter(r => r.score >= similarityThreshold).length
      : 0;

    const existingBelow = stage === 3 && similarityThreshold !== null
      ? results
          .filter(r => r.score < similarityThreshold)
          .map(r => ({ hash: r.analysis_hash, score: r.score }))
      : null;

    const alreadySearched = stage === 1 ? null : (searchedHashes.length > 0 ? searchedHashes : null);

    try {
      await calculateAnalysisSimilars(
        analysisHash, 10, pageToSearch, stage, alreadySearched, existingAboveCount, existingBelow
      );
    } catch (error) {
      console.error('Error triggering similar search:', error);
      setCalculating(false);
      return;
    }

    const poll = setInterval(async () => {
      try {
        const result = await getAnalysisSimilars(analysisHash);
        if (result.cached && result.data && result.data.stage >= stage) {
          if (result.data.validating) {
            setValidating(true);
            return;
          }
          clearInterval(poll);
          const newResults = result.data.results || [];
          const newTopOverall = result.data.top_overall || [];
          const vlmRejected = new Set(result.data.vlm_rejected || []);
          if (stage > 1) {
            const existingHashes = new Set(results.map(r => r.analysis_hash));
            const uniqueNew = newResults.filter(r => !existingHashes.has(r.analysis_hash));
            setResults(prev => {
              const cleaned = vlmRejected.size > 0 ? prev.filter(r => !vlmRejected.has(r.analysis_hash)) : prev;
              return [...cleaned, ...uniqueNew];
            });
            setTopOverall(prev => {
              const seen = new Set(prev.map(r => r.analysis_hash));
              const merged = [...prev];
              for (const item of newTopOverall) {
                if (!seen.has(item.analysis_hash)) {
                  merged.push(item);
                  seen.add(item.analysis_hash);
                }
              }
              return merged.sort((a, b) => b.score - a.score);
            });
          } else {
            setResults(newResults);
            setTopOverall(newTopOverall);
          }
          setSearchedHashes(result.data.searched_hashes || []);
          setCurrentStage(stage);
          setCalculating(false);
          setValidating(false);
        }
      } catch (error) {
        console.error('Error polling similar results:', error);
      }
    }, 3000);
  };

  const handleHeuristicSearch = async () => {
    const pageKey = selectedPage || 'default';
    setHeuristicByPage(prev => ({ ...prev, [pageKey]: { status: 'searching', results: [] } }));

    try {
      const resultsHashes = new Set(results.map(r => r.analysis_hash));
      const rejectedShown = topOverall.filter(r => !resultsHashes.has(r.analysis_hash)).slice(0, 10);
      const existingHashes = [
        ...results.map(r => r.analysis_hash),
        ...rejectedShown.map(r => r.analysis_hash),
      ];
      const pageToSearch = hasMultiplePages ? selectedPage : undefined;

      const data = await startHeuristicSearch(analysisHash, {
        existingHashes,
        pageNumber: pageToSearch,
        onValidating: () => setHeuristicByPage(prev => ({
          ...prev, [pageKey]: { ...prev[pageKey], status: 'validating' }
        })),
      });

      setHeuristicByPage(prev => ({
        ...prev, [pageKey]: { status: 'completed', results: data.matches || [] }
      }));
    } catch (err) {
      console.error('Error in heuristic search:', err);
      setHeuristicByPage(prev => ({ ...prev, [pageKey]: { status: 'idle', results: [] } }));
    }
  };

  const getImageKey = (item) => item.matched_page ? `${item.analysis_hash}_p${item.matched_page}` : item.analysis_hash;

  const handleExpand = async (item) => {
    const imageKey = getImageKey(item);

    if (expandedItem === imageKey) {
      setExpandedItem(null);
      return;
    }

    setExpandedItem(imageKey);

    if (!imageUrls[imageKey]) {
      try {
        let url = `${process.env.REACT_APP_API_URL}/v1/analyses/${item.analysis_hash}/image?include_deleted=true`;
        if (item.matched_page) url += `&page=${item.matched_page}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setImageUrls(prev => ({ ...prev, [imageKey]: blobUrl }));
        } else {
          setImageUrls(prev => ({ ...prev, [imageKey]: 'error' }));
        }
      } catch (error) {
        console.error('Error loading image:', error);
        setImageUrls(prev => ({ ...prev, [imageKey]: 'error' }));
      }
    }
  };

  const handleViewAnalysis = (hash) => {
    if (onViewAnalysis) {
      onViewAnalysis(hash);
    }
  };

  const formatScore = (score) => {
    return (score * 100).toFixed(1) + '%';
  };

  if (analysisStatus !== 'completed') {
    return null;
  }

  if (loading) {
    return (
      <div className="similar-panel">
        <h3>Planos Similares</h3>
        <div className="similar-loading">Cargando...</div>
      </div>
    );
  }

  const nextStage = currentStage + 1;
  const stageLabels = {
    1: 'Buscar en carpeta',
    2: 'Buscar recientes',
    3: 'Buscar en resto'
  };
  const muySimilaresCount = similarityThreshold !== null ? results.filter(r => r.score >= similarityThreshold).length : 0;
  const visibleCount = currentStage >= 3
    ? muySimilaresCount + Math.min(results.length - muySimilaresCount, Math.max(0, 10 - muySimilaresCount))
    : muySimilaresCount;

  const visibleHashes = new Set(results.map(r => r.analysis_hash));
  const rejectedDrawings = currentStage >= 3
    ? topOverall.filter(r => !visibleHashes.has(r.analysis_hash)).slice(0, 10)
    : [];

  return (
    <div className="similar-panel">
      <div className="similar-header">
        <h3>Planos Similares</h3>
        {currentStage > 0 && (
          <button className="btn-reset-similar" onClick={() => { setResults([]); setSearchedHashes([]); setCurrentStage(0); }}>
            ↺
          </button>
        )}
      </div>

      {hasMultiplePages && (
        <div className="similar-page-selector">
          <span>Pagina:</span>
          <select
            value={selectedPage || ''}
            onChange={(e) => {
              setSelectedPage(Number(e.target.value));
              setResults([]);
              setSearchedHashes([]);
              setCurrentStage(0);
            }}
            disabled={calculating}
          >
            {drawingPageNumbers.map(pageNum => (
              <option key={pageNum} value={pageNum}>
                {pageNum}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="similar-stage-buttons">
        {nextStage <= 3 && (
          <button
            className={`btn-stage btn-stage-${nextStage}`}
            onClick={() => handleSearch(nextStage)}
            disabled={calculating}
          >
            {validating ? <><span className="btn-spinner"></span>Validando resultados...</> : calculating ? <><span className="btn-spinner"></span>Buscando...</> : stageLabels[nextStage]}
          </button>
        )}
        {currentStage > 0 && !calculating && (
          <span className="similar-stage-hint">
            {visibleCount} resultado{visibleCount !== 1 ? 's' : ''} · {searchedHashes.length} analizados
          </span>
        )}
      </div>

      {currentStage > 0 && currentStage < 3 && visibleCount === 0 ? (
        <div className="similar-no-results">
          No se encontraron planos muy similares. {currentStage === 1 ? 'Prueba buscando en archivos recientes.' : 'Prueba buscando en el resto de la colección.'}
        </div>
      ) : results.length > 0 && similarityThreshold !== null ? (
        <div className="similar-list">
          {results.filter(item => item.score >= similarityThreshold).length > 0 && (
            <div className="similar-group">
              <div className="similar-group-title very-similar">Muy similares</div>
              {results.filter(item => item.score >= similarityThreshold).sort((a, b) => b.score - a.score).slice(0, 10).map((item) => (
                <SimilarItem
                  key={getImageKey(item)}
                  item={item}
                  expanded={expandedItem === getImageKey(item)}
                  imageUrl={imageUrls[getImageKey(item)]}
                  onExpand={() => handleExpand(item)}
                  onView={() => handleViewAnalysis(item.analysis_hash)}
                  onImageClick={(url) => setModalImage(url)}
                  formatScore={formatScore}
                  showScore
                />
              ))}
            </div>
          )}
          {currentStage >= 3 && results.filter(item => item.score < similarityThreshold).length > 0 && results.filter(item => item.score >= similarityThreshold).length < 10 && (
            <div className="similar-group">
              <div className="similar-group-title related">Más cercanos</div>
              {results.filter(item => item.score < similarityThreshold).sort((a, b) => b.score - a.score).slice(0, 10 - Math.min(results.filter(item => item.score >= similarityThreshold).length, 10)).map((item) => (
                <SimilarItem
                  key={getImageKey(item)}
                  item={item}
                  expanded={expandedItem === getImageKey(item)}
                  imageUrl={imageUrls[getImageKey(item)]}
                  onExpand={() => handleExpand(item)}
                  onView={() => handleViewAnalysis(item.analysis_hash)}
                  onImageClick={(url) => setModalImage(url)}
                  formatScore={formatScore}
                />
              ))}
            </div>
          )}
          {rejectedDrawings.length > 0 && (
            <div className="similar-group">
              <div className="similar-group-title rejected">Otras piezas parecidas</div>
              {rejectedDrawings.map((item) => (
                <SimilarItem
                  key={getImageKey(item)}
                  item={item}
                  expanded={expandedItem === getImageKey(item)}
                  imageUrl={imageUrls[getImageKey(item)]}
                  onExpand={() => handleExpand(item)}
                  onView={() => handleViewAnalysis(item.analysis_hash)}
                  onImageClick={(url) => setModalImage(url)}
                  formatScore={formatScore}
                />
              ))}
            </div>
          )}
        </div>
      ) : currentStage >= 3 && visibleCount === 0 && rejectedDrawings.length === 0 ? (
        <div className="similar-no-results">
          No se encontraron planos similares
        </div>
      ) : currentStage >= 3 && rejectedDrawings.length > 0 ? (
        <div className="similar-list">
          <div className="similar-group">
            <div className="similar-group-title rejected">Otras piezas parecidas</div>
            {rejectedDrawings.map((item) => (
              <SimilarItem
                key={getImageKey(item)}
                item={item}
                expanded={expandedItem === getImageKey(item)}
                imageUrl={imageUrls[getImageKey(item)]}
                onExpand={() => handleExpand(item)}
                onView={() => handleViewAnalysis(item.analysis_hash)}
                onImageClick={(url) => setModalImage(url)}
                formatScore={formatScore}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Búsqueda heurística */}
      {currentStage >= 3 && !calculating && (
        <div className="similar-heuristic">
          {heuristicStatus === 'idle' && (
            <button className="btn-stage btn-heuristic" onClick={handleHeuristicSearch}>
              Búsqueda heurística
            </button>
          )}
          {heuristicStatus === 'searching' && (
            <span className="heuristic-status"><span className="btn-spinner"></span> Buscando candidatos...</span>
          )}
          {heuristicStatus === 'validating' && (
            <span className="heuristic-status"><span className="btn-spinner"></span> Validando con IA...</span>
          )}
          {heuristicStatus === 'completed' && (
            heuristicResults.length > 0 ? (
              <div className="similar-group">
                <div className="similar-group-title heuristic">Búsqueda heurística ({heuristicResults.length})</div>
                {heuristicResults.map((item) => {
                  const hItem = { ...item, analysis_hash: item.hash };
                  return (
                  <SimilarItem
                    key={`heuristic-${getImageKey(hItem)}`}
                    item={hItem}
                    expanded={expandedItem === getImageKey(hItem)}
                    imageUrl={imageUrls[getImageKey(hItem)]}
                    onExpand={() => handleExpand(hItem)}
                    onView={() => handleViewAnalysis(item.hash)}
                    onImageClick={(url) => setModalImage(url)}
                    formatScore={formatScore}
                  />
                  );
                })}
              </div>
            ) : (
              <div className="similar-no-results">No se encontraron candidatos adicionales</div>
            )
          )}
        </div>
      )}

      {modalImage && (
        <div className="similar-modal" onClick={() => setModalImage(null)}>
          <div className="similar-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="similar-modal-close" onClick={() => setModalImage(null)}>
              X
            </button>
            <img src={modalImage} alt="Vista ampliada" />
          </div>
        </div>
      )}
    </div>
  );
};

const SimilarItem = ({ item, expanded, imageUrl, onExpand, onView, onImageClick, formatScore, showScore }) => (
  <div className="similar-item">
    <div
      className={`similar-item-header ${expanded ? 'expanded' : ''}`}
      onClick={onExpand}
    >
      <div className="similar-item-info">
        {showScore && <span className="similar-score">{formatScore(item.score)}</span>}
      </div>
      <div className="similar-item-main">
        <span className="similar-filename" title={item.filename}>
          {item.filename}
        </span>
        {item.reference && (
          <span className="similar-item-reference" onClick={(e) => e.stopPropagation()}>
            <ReferenceHistory reference={item.reference} count={item.reference_count} />
          </span>
        )}
      </div>
      <span className="similar-expand-icon">
        {expanded ? '\u25BC' : '\u25B6'}
      </span>
    </div>

    {expanded && (
      <div className="similar-item-content">
        {imageUrl === 'error' ? (
          <div className="similar-thumbnail-error">Imagen no disponible</div>
        ) : imageUrl ? (
          <div className="similar-thumbnail" onClick={() => onImageClick(imageUrl)}>
            <img src={imageUrl} alt={item.filename} />
          </div>
        ) : (
          <div className="similar-thumbnail-loading">Cargando imagen...</div>
        )}
        <button className="btn-view-analysis" onClick={onView}>
          Ver analisis
        </button>
      </div>
    )}
  </div>
);

export default SimilarDrawingsPanel;
